---
title: Capture Cursor Sessions with Checkpoints
description: The Entire CLI now supports Cursor, capturing checkpoints and session logs. OpenCode joins the supported agents, and commits and pushes get faster.
category: Changelog
author: Marvin
---

Beep, boop. Welcome to Dispatch 0x0003.

In a world where prompts are the new programming language, why not share them like code? The Entire CLI creates a semantic reasoning layer that automatically stores agent context in Git. Resulting in what we call, Checkpoints!

With help from the community, we shipped several improvements this week, including support for our first IDE. Today, we're bringing the [Entire CLI to Cursor](https://github.com/entireio/cli), enabling users to bind agent context to every push and explore, trace and share their intent. The rewind functionality and the resume command are not available, but Checkpoints are created and session logs captured. Thank you to [@9bany](https://github.com/9bany) for your early contributions to make this possible!

Here's what else changed:

## OpenCode Support

- Since this week, the Entire CLI supports OpenCode.
- Fixed an issue with OpenCode and OpenAI Codex models not creating checkpoints when existing files were changed.
  - Thank you to [@jaxondk](https://github.com/jaxondk) for requesting OpenCode support, and to [@ammarateya](https://github.com/ammarateya), [@Avyukth](https://github.com/Avyukth), and [@MementoMori123](https://github.com/MementoMori123), whose OpenCode PRs helped validate the design!

## Faster Checkpoints

- Introduced caching and optimized work tree updates to improve performance on Git commits and Git pushes.
- Squash-merged commits with multiple `entire-checkpoint` trailers correctly link to all their checkpoints on [entire.io](https://entire.io).
  - Thank you to [@FSM1](https://github.com/FSM1) for reporting the squash+delete workflow gap!
- To fix carry-forward false positives, orphaned shadow branches are no longer created when a user replaces agent content.
- Stale sessions no longer incorrectly condense into every commit.
  - Thanks to [@BootstrapBits](https://github.com/BootstrapBits) for the detailed concurrent session race analysis!
- Stuck checkpoint IDs and "no active sessions" errors are resolved.
  - HT [@lakson-llc](https://github.com/lakson-llc), [@daeshawnballard](https://github.com/daeshawnballard), and [@wmurphyrd](https://github.com/wmurphyrd)!
- O(depth) tree surgery replaces O(N) flatten-and-rebuild for both metadata branch and shadow branch writes
- Extracted magic numbers to named constants for improved code quality.
  - Thanks to [@ishaan812](https://github.com/ishaan812)!

## Further Improvements

- **Web viewer**
  - Added a searchable breadcrumb combobox making it easier to search and switch repos within the org.
  - In the spirit of the community, public repos are available on entire.io without logging in!
  - Fixed the tool calls section to be hidden for agents that don't report them.
  - False positive trail merge detection is fixed for new branches.
- **Fresh clone support**
  - `entire enable` now creates local metadata branch from remote when available, preserving checkpoints on fresh clones
- **Linking commits to your checkpoints**
  - The checkpoint linking question when doing `git commit -m "commit message"` now has an "Always" option.
- **Depreciating "Auto-Commit"**
  - To reduce complexity and with feedback from the community, we decided to cut the auto-commit command. We think telling the agent to actually commit for you is much more effective.
- **Hook Scoping**
  - Entire only creates `.entire/logs/` folders in repos where Entire is enabled. Git hooks only run in repos where Entire is enabled.
    - Thanks to [@knowsuchagency](https://github.com/knowsuchagency)!
- **Secret Redaction**
  - Secret redaction is improved to not redact legitimate code.
- **Unicode and Internationalization**
  - UTF-8 truncation no longer produces garbled text for CJK/emoji characters.
    - Thanks to [@wasabeef](https://github.com/wasabeef) for identifying the root cause!
- **Session Hygiene**
  - Gitignored files (e.g. `node_modules`) are no longer tracked in session metadata.
  - Resume sessions are now sorted by creation time and stale session files are automatically cleaned up.
- **Nix Package**
  - Entire is available as a Nix package: [nixpkgs/entire](https://search.nixos.org/packages?channel=unstable&query=entire&show=entire).
    - Thanks to [@evanlhatch](https://github.com/evanlhatch) for requesting this and [@DieracDelta](https://github.com/DieracDelta) for the interim fork!

Keep the contributions coming. Next week, we continue to expand our capabilities and integrate the Entire CLI across the broader agent ecosystem.

That's all for now, rebels.

Boop.
